import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { 
  Users, 
  Car, 
  Accessibility, 
  UtensilsCrossed, 
  Speaker, 
  TableProperties,
  CheckCircle,
  ArrowRight,
  MapPin
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AboutPage() {
  const [hallInfo, setHallInfo] = useState(null);

  useSEO({
    title: "About The Elizabethan Centre | Facilities & Capacity | Whaplode Drove",
    description: "Discover The Elizabethan Centre's facilities - seating for 150, standing capacity 200, full kitchen, bar, stage, disabled access and ample parking. Perfect venue for any event in Lincolnshire.",
    canonical: "https://www.elizabethancentre.co.uk/about"
  });

  useEffect(() => {
    const fetchHallInfo = async () => {
      try {
        const response = await axios.get(`${API}/hall-info`);
        setHallInfo(response.data);
      } catch (error) {
        console.error("Error fetching hall info:", error);
      }
    };
    fetchHallInfo();
  }, []);

  const facilityIcons = {
    "Main Hall": <Speaker className="w-6 h-6" />,
    "Kitchen facilities": <UtensilsCrossed className="w-6 h-6" />,
    "Car parking": <Car className="w-6 h-6" />,
    "Disabled access": <Accessibility className="w-6 h-6" />,
    "Tables and chairs": <TableProperties className="w-6 h-6" />,
    "Stage area": <Speaker className="w-6 h-6" />
  };

  const eventTypes = [
    "Weddings & Receptions",
    "Birthday Parties",
    "Anniversary Celebrations",
    "Community Meetings",
    "Club Events",
    "Fitness Classes",
    "Markets & Fairs",
    "Fundraising Events",
    "Private Functions",
    "Corporate Meetings"
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#F5F5F0]" data-testid="about-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block bg-[#14532D]/10 text-[#14532D] text-sm font-medium px-4 py-1.5 rounded-full mb-6">
              About the Hall
            </span>
            <h1 className="heading-xl mb-6">
              The Elizabethan Centre
            </h1>
            <p className="text-xl text-[#292524] leading-relaxed">
              A welcoming community hall in the heart of Whaplode Drove, serving the local 
              community for generations. Available for hire for all types of events.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding" data-testid="about-content">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left Column - Image & Description */}
            <div>
              <div className="rounded-2xl overflow-hidden mb-8 shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ec-events-booking/artifacts/k3fmts5h_1000015917.jpg" 
                  alt="Wedding reception at Whaplode Drove Social Club Hall" 
                  className="w-full h-[400px] object-cover"
                />
              </div>
              <div className="prose prose-lg max-w-none">
                <p className="text-body mb-6">
                  The Elizabethan Centre has been a cornerstone of the Whaplode Drove community 
                  for many years. Our well-maintained hall provides an ideal venue for celebrations, 
                  meetings, and community gatherings.
                </p>
                <p className="text-body mb-6">
                  Whether you're planning a wedding reception, birthday party, community meeting, 
                  or any other event, our versatile space can be configured to meet your needs.
                </p>
                <p className="text-body">
                  We pride ourselves on offering affordable rates while maintaining a clean, 
                  welcoming environment for all our visitors.
                </p>
              </div>
            </div>

            {/* Right Column - Details */}
            <div>
              {/* Capacity Card */}
              <Card className="card-heritage mb-8" data-testid="capacity-card">
                <CardContent className="p-8">
                  <h3 className="heading-md mb-6 flex items-center gap-3">
                    <Users className="w-8 h-8 text-[#14532D]" />
                    Capacity
                  </h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="bg-[#F5F5F0] rounded-xl p-6 text-center">
                      <span className="block text-4xl font-bold font-mono text-[#14532D] mb-2">
                        {hallInfo?.capacity?.seated || 150}
                      </span>
                      <span className="text-sm text-[#78716C]">Seated Guests</span>
                    </div>
                    <div className="bg-[#F5F5F0] rounded-xl p-6 text-center">
                      <span className="block text-4xl font-bold font-mono text-[#14532D] mb-2">
                        {hallInfo?.capacity?.standing || 200}
                      </span>
                      <span className="text-sm text-[#78716C]">Standing Reception</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Facilities */}
              <Card className="card-heritage mb-8" data-testid="facilities-card">
                <CardContent className="p-8">
                  <h3 className="heading-md mb-6">Facilities</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {hallInfo?.facilities?.map((facility) => (
                      <div 
                        key={facility}
                        className="flex items-center gap-3 p-3 bg-[#F5F5F0] rounded-lg"
                      >
                        <span className="text-[#14532D]">
                          {facilityIcons[facility] || <CheckCircle className="w-6 h-6" />}
                        </span>
                        <span className="text-sm font-medium text-[#1C1917]">{facility}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Location */}
              <Card className="card-heritage" data-testid="location-card">
                <CardContent className="p-8">
                  <h3 className="heading-md mb-6 flex items-center gap-3">
                    <MapPin className="w-8 h-8 text-[#14532D]" />
                    Location
                  </h3>
                  <p className="text-body mb-4">
                    {hallInfo?.address || "Whaplode Drove, Spalding, Lincolnshire"}
                  </p>
                  <p className="text-subtle">
                    Easy to find with ample free parking available on site.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="section-padding bg-[#F5F5F0]" data-testid="event-types-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">Perfect For</h2>
            <p className="text-body max-w-2xl mx-auto">
              Our versatile hall is suitable for a wide range of events
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {eventTypes.map((type) => (
              <span 
                key={type}
                className="px-5 py-2.5 bg-white rounded-full text-sm font-medium text-[#1C1917] border border-[#E7E5E4] hover:border-[#14532D] hover:text-[#14532D] transition-colors cursor-default"
                data-testid={`event-type-${type.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {type}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Terms Section */}
      <section className="section-padding" data-testid="terms-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-lg mb-8 text-center">Terms of Hire</h2>
            <Card className="card-heritage">
              <CardContent className="p-8">
                <ul className="space-y-4">
                  {hallInfo?.terms?.map((term) => (
                    <li key={term} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#15803D] mt-0.5 flex-shrink-0" />
                      <span className="text-body">{term}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#14532D]" data-testid="about-cta">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl font-bold font-serif text-white mb-6">
            Ready to Book?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Check our availability calendar and submit your booking request online.
          </p>
          <Link to="/book">
            <Button className="bg-white text-[#14532D] hover:bg-[#F5F5F0] h-12 px-8 text-base rounded-full font-medium">
              Book the Hall
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
